import {
  EuiCallOut,
  EuiEmptyPrompt,
  EuiFormRow,
  EuiLoadingChart,
  EuiMarkdownFormat,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@inscopix/ideas-eui";
import { Field, FieldProps } from "formik";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";

import { PublishOptionsFirstVersion } from "./PublishOptionsFirstVersion";
import { PublishOptionsExistingVersion } from "./PublishOptionsExistingVersion";
import { fetchChangelogTemplate } from "components/ToolEditor/api/fetchChangelogTemplate";
import { useEffect, useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { captureException } from "@sentry/react";
import { ButtonCopyToolVersionDocs } from "components/ButtonCopyToolVersionDocs/ButtonCopyToolVersionDocs";

export type ToolEditorPublishValues = {
  version: "major" | "minor" | "patch" | "first_version";
  changelog?: string;
};

export const ToolEditorPublish = () => {
  const toolVersionId = useToolEditorContext((s) => s.toolVersionId);
  const hasTestTable = useToolEditorContext((s) => s.testTable !== null);
  const changelogTemplate = useToolEditorContext((s) => s.changelogTemplate);
  const setChangelogTemplate = useToolEditorContext(
    (s) => s.setChangelogTemplate,
  );

  const publishOptions = useToolEditorContext((s) => s.publishOptions);

  const [changelogLoading, setChangelogLoading] = useState(true);

  // when component loads, automatically fetch changelog template to populate field  in formik state
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await fetchChangelogTemplate(toolVersionId); // example toolVersionId
        if (response.changelog_template !== "") {
          setChangelogTemplate(response.changelog_template);
        } else {
          setChangelogTemplate(null);
        }
      } catch (error) {
        addUtilityToastFailure(
          "Failed to load automatically generated changelog.",
        );
        captureException(error);
      } finally {
        setChangelogLoading(false);
      }
    };
    void fetchTemplate();
  }, [setChangelogTemplate, toolVersionId]);

  return (
    <div style={{ width: 800, margin: "0 auto" }}>
      {!hasTestTable && (
        <>
          <EuiCallOut color="warning" title="Warning" iconType="warning">
            You are about to publish a tool with no test data. It is highly
            recommended to completed the test step to ensure your tool is
            properly configured to be executed in IDEAS.
          </EuiCallOut>
          <EuiSpacer />
        </>
      )}
      <Field name={"changelog" satisfies keyof ToolEditorPublishValues}>
        {({
          field,
          meta,
        }: FieldProps<ToolEditorPublishValues["changelog"]>) => {
          return (
            <EuiFormRow
              fullWidth
              label="Changelog"
              error={meta.error}
              isInvalid={meta.error !== undefined}
            >
              <EuiTextArea
                id={"changelog" satisfies keyof ToolEditorPublishValues}
                fullWidth
                placeholder="Provide an optional summary of the changes made to your tool in this version"
                {...field}
              />
            </EuiFormRow>
          );
        }}
      </Field>

      {changelogLoading ? (
        <EuiEmptyPrompt
          icon={<EuiLoadingChart size="xl" mono />}
          title={<h2>Detecting configuration changes...</h2>}
          titleSize="xs"
        />
      ) : (
        <EuiPanel hasBorder>
          {changelogTemplate === null ? (
            <EuiText color="subdued" size="s">
              <p>
                <em>No configuration changes detected</em>
              </p>
            </EuiText>
          ) : (
            <EuiMarkdownFormat textSize="xs">
              {changelogTemplate}
            </EuiMarkdownFormat>
          )}
        </EuiPanel>
      )}

      <EuiSpacer size="m" />

      <EuiCallOut>
        If you would a formatted markdown template of the configured parameters
        and outputs of this tool version to use in your documentation, you can
        copy it to your clipboard using the button below.
        <EuiSpacer size="s" />
        <ButtonCopyToolVersionDocs toolVersionId={toolVersionId} size="s" />
      </EuiCallOut>

      <EuiSpacer size="m" />
      <Field name={"version" satisfies keyof ToolEditorPublishValues}>
        {({ meta }: FieldProps<ToolEditorPublishValues["version"]>) => {
          return (
            <EuiFormRow
              fullWidth
              label="Version"
              error={meta.error}
              isInvalid={meta.error !== undefined}
            >
              {"first_version" in publishOptions ? (
                <PublishOptionsFirstVersion />
              ) : (
                <PublishOptionsExistingVersion options={publishOptions} />
              )}
            </EuiFormRow>
          );
        }}
      </Field>
    </div>
  );
};
